// =============================================================================
// prechargementVues.js — charger le code d'une vue AVANT d'y aller
// (refonte « Mosaïque vivante », lot L6, 28/09/2026 ; synthèse §6.5).
//
// Aramis découpe ses vues en morceaux chargés à la demande (81 enveloppes
// paresseuses dans App.jsx). Aller sur une vue jamais ouverte montrait le
// pictogramme de chargement à la place de la page, puis la page : un flash à
// chaque première visite, et un instantané « après » vide pour la transition.
//
// Désormais :
//   • à l'INTENTION (survol de 80 ms au pointeur, appui de 80 ms au doigt,
//     focus clavier) sur une entrée de menu, le morceau de la vue part en
//     téléchargement ; au clic, il est presque toujours là ;
//   • navTo ATTEND le morceau (s'il manque) en gardant la vue courante à
//     l'écran, avec la barre de progression de 2 px, puis bascule d'un coup
//     (et peut donc animer la transition) ;
//   • le pictogramme ne s'affiche plus qu'au-delà de 300 ms (PictoEnioAnime).
//
// App.jsx ENREGISTRE ici la table « vue → composants » (bloc généré
// CHUNKS_VUES, tenu à jour par src/prechargementVues.test.js) : les composants
// vivent dans App.jsx, ce module ne les importe pas (pas de cycle).
// =============================================================================
import { useEffect, useRef } from "react";

let resolveur = null;

/** App.jsx : (vue) => [composants préchargeables] | [] (rien à charger) | null (inconnue). */
export function enregistrerChunksVues(fn) { resolveur = typeof fn === "function" ? fn : null; }

export function chunksDeVue(vue) {
  if (!resolveur || !vue) return null;
  try { return resolveur(vue); } catch { return null; }
}

/** true : le code est là ; false : il manque ; null : vue inconnue de la table. */
export function vuePrete(vue) {
  const c = chunksDeVue(vue);
  if (c === null || c === undefined) return null;
  return c.every((x) => !x || typeof x.estCharge !== "function" || x.estCharge());
}

/** Lance le chargement ; renvoie une promesse (résolue même en cas d'échec :
 *  l'erreur réelle remontera, au rendu, par la frontière de la vue), ou null
 *  s'il n'y a rien à charger. */
export function prechargerVue(vue) {
  const c = (chunksDeVue(vue) || []).filter((x) => x && typeof x.preload === "function" && !(x.estCharge && x.estCharge()));
  if (!c.length) return null;
  return Promise.all(c.map((x) => Promise.resolve(x.preload()).catch(() => null)));
}

/**
 * Préchargement à l'intention, pour les entrées de menu. Renvoie une fonction
 * (route) => gestionnaires à étaler sur le bouton : survol de 80 ms (pointeur
 * fin), appui de 80 ms (doigt), focus (clavier).
 */
export function usePrechargementIntention(delai = 80) {
  const minuteur = useRef(null);
  useEffect(() => () => clearTimeout(minuteur.current), []);
  const armer = (route) => {
    clearTimeout(minuteur.current);
    minuteur.current = setTimeout(() => { prechargerVue(route); }, delai);
  };
  const desarmer = () => clearTimeout(minuteur.current);
  return (route) => ({
    onPointerEnter: (e) => { if (e.pointerType === "mouse") armer(route); },
    onPointerLeave: desarmer,
    onPointerDown: () => armer(route),
    onPointerCancel: desarmer,
    onFocus: () => { prechargerVue(route); },
  });
}
